import React, { Fragment } from 'react';

import { BookingInquiryContactDisplay } from '@components/utilities/BookingInquiry/BookingInquiryContact';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { getCityLabelByName } from '@data/cities';
import { routePlanStrings } from '@data/routePlan';
import {
  type InquiryDetailCargos,
  type CargoLoose,
  getSummaryOfCargoLoose,
} from '@models/BookingInquiry';
import type { SimulatorResult } from '@models/RoutePlan';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { sum } from 'lodash-es';

const strings = createStrings({
  ja: {
    english: '英語',
    cargosContent: '荷物の内容',
    warehouse: '倉庫',
    warehouseHint: () => (
      <>
        ＊ 倉庫の住所及び連絡先は、予約受付後SHIPPER（輸出者）にご案内いたします。
        <br />
        SHIPPER（輸出者）には、出荷前に「ラベル」を印刷し、荷物全量へ貼付して頂きます。
        <br />
        倉庫で荷受けする際には、弊社が事前に発行した「货物进仓通知书」（倉庫搬入事前受付確認書）を持参して頂きます。
        <br />
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  bookingInquiryCargosDisplayContainer: {
    ...common.smallerText,
    ...common.textGray900,
    display: 'grid',
    columnGap: 32,
    '&$lwh': {
      gridTemplateColumns: 'repeat(4, auto)',
    },
    '&$wv': {
      gridTemplateColumns: 'repeat(4, auto)',
    },
    '&$lwh$inline': {
      gridTemplateColumns: 'minmax(auto, 350px) repeat(4, auto)',
    },
    '&$wv$inline': {
      gridTemplateColumns: 'minmax(auto, 350px) repeat(4, auto)',
    },
    '& > *[role="head"]': {
      whiteSpace: 'pre',
      ...common.fontWeight600,
    },
    '& > div': {
      wordBreak: 'break-all',
      marginBottom: 4,
    },
  },
  bookingInquiryCargosDisplayCargoSummary: {
    ...common.smallerText,
    ...common.textGray700,
    ...common.fontWeight600,
    marginTop: 4,
    paddingTop: 4,
    textAlign: 'end',
    borderTop: `1px dashed ${theme.palette.grey[300]}`,
  },
  lwh: {},
  wv: {},
  inline: {},
  obsolete: {},
}));

export interface BookingInquiryCargosDisplayProps {
  cargos: InquiryDetailCargos;
  simulatorResult?: SimulatorResult;
  groupThreshold: number;
  cargosThreshold: number;
  // Control whether pickup address/warehouse and carogs are displayed in the same line
  variant?: 'inline' | 'block';
  type?: 'client' | 'admin';
  obsolete?: boolean;
}

export const BookingInquiryCargosDisplay: React.FC<BookingInquiryCargosDisplayProps> = (props) => {
  const {
    cargos,
    simulatorResult,
    groupThreshold,
    cargosThreshold,
    variant = 'block',
    obsolete,
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const cargosByPickupAddress = cargos.cargos_by_pickup_address?.slice(0, groupThreshold);
  const cargosByPort = cargos.cargos_by_port?.slice(0, groupThreshold);

  const containsLWH =
    cargosByPickupAddress?.some((s) => s.cargos.some((c) => c.tag === 'LWH')) ||
    cargos.cargos_by_port?.some((p) => p.cargos.some((c) => c.tag === 'LWH'));

  const cargoColumns = 4;

  const renderCargoSummary = (cargos: CargoLoose[]) => {
    const summaries = cargos.map(getSummaryOfCargoLoose);

    const unit = sum(summaries.map((s) => s.unit));
    const cbm = sum(summaries.map((s) => s.cbm));
    const kg = sum(summaries.map((s) => s.kg));

    return (
      <div
        className={classes.bookingInquiryCargosDisplayCargoSummary}
        style={{ gridColumn: `span ${cargoColumns}` }}
      >
        Shipment Total: {unit || '-'} UNITS, {cbm.toFixed(3)} CBM, {kg.toFixed(3)} KGS
      </div>
    );
  };

  const renderSubtitle = (title: React.ReactNode, span: number) => {
    return (
      <div
        className={clsx(
          classes.smallerText,
          obsolete ? classes.textGray600 : classes.textPrimary,
          classes.fontWeight600,
        )}
        style={{ paddingBottom: '0.1rem', gridColumn: `span ${span}` }}
      >
        {title}
      </div>
    );
  };

  const renderCargoHead = (tag: 'LWH' | 'WV') => {
    return (
      <>
        <div role="head">
          {bookingInquiryStrings.cargoName}
          {'('}
          {strings.english}
          {')'}
        </div>
        {tag === 'WV' && (
          <>
            <div role="head">{bookingInquiryStrings.cargoNum}</div>
            <div role="head">{bookingInquiryStrings.totalWeight}</div>
            <div role="head">{bookingInquiryStrings.totalVolume}</div>
          </>
        )}

        {tag === 'LWH' && (
          <>
            <div role="head">{bookingInquiryStrings.cargoNum}</div>
            <div role="head">{bookingInquiryStrings.cargoSize}</div>
            <div role="head">{bookingInquiryStrings.weight}</div>
          </>
        )}
      </>
    );
  };

  const renderCargo = (cargo: CargoLoose, i: number) => {
    return (
      <Fragment key={i}>
        <div>{cargo.name}</div>
        <div>{cargo.num}</div>

        {cargo.tag === 'LWH' && (
          <>
            <div>
              {cargo.length}
              {'×'}
              {cargo.width}
              {'×'}
              {cargo.height}
            </div>
            <div>{cargo.weight}</div>
          </>
        )}
        {cargo.tag === 'WV' && (
          <>
            <div>{cargo.total_weight}</div>
            <div>{cargo.total_volume}</div>
            {/* Placeholder to fill columns */ containsLWH && <div />}
          </>
        )}
      </Fragment>
    );
  };

  const renderCargoGroup = (
    i: number,
    length: number,
    mainTitle: string,
    cargosTitle: string,
    renderMain: (style: {
      gridRow: string | undefined;
      gridColumn: string | undefined;
    }) => React.ReactNode,
    cargos: CargoLoose[],
  ) => {
    const displayedCargos = cargos.slice(0, cargosThreshold);
    const cargosCount = displayedCargos.length;

    const cargoRows = cargosCount + 2 + (simulatorResult ? 1 : 0);

    const mainStyle = {
      gridColumn: `span ${variant === 'inline' ? 1 : cargoColumns}`,
      gridRow: variant === 'block' ? undefined : `span ${cargoRows}`,
    };

    return (
      <Fragment key={i}>
        {renderSubtitle(mainTitle, variant === 'inline' ? 1 : cargoColumns)}

        {variant === 'inline' && renderSubtitle(cargosTitle, cargoColumns)}

        {renderMain(mainStyle)}

        {variant === 'block' && renderSubtitle(cargosTitle, cargoColumns)}
        {renderCargoHead(cargos[0]?.tag ?? 'LWH')}
        {displayedCargos.map((c, i) => renderCargo(c, i))}

        {renderCargoSummary(cargos)}

        {i !== length - 1 && (
          <>
            {variant === 'inline' && <div style={{ gridColumn: `span ${cargoColumns}` }} />}
            {variant === 'block' && (
              <div style={{ gridColumn: `span ${cargoColumns}`, height: '1rem' }} />
            )}
          </>
        )}
      </Fragment>
    );
  };

  return (
    <div
      className={clsx(
        classes.bookingInquiryCargosDisplayContainer,
        containsLWH ? classes.lwh : classes.wv,
        variant === 'inline' && classes.inline,
        obsolete && classes.obsolete,
      )}
    >
      {cargosByPickupAddress?.map((pickup, i) => {
        return renderCargoGroup(
          i,
          cargosByPickupAddress.length,
          `${bookingInquiryStrings.pickupAddress} ${i + 1}`,
          `${bookingInquiryStrings.pickupAddress} ${i + 1} - ${strings.cargosContent}`,
          (style) => (
            <BookingInquiryContactDisplay
              contactType="pickup_address"
              contact={pickup.pickup_address}
              style={{
                ...style,
                // This will make left-side take (n + 2) rows while right (n + 1) rows. This makes the right rows shrink to top
                marginBottom: '1rem',
              }}
            />
          ),
          pickup.cargos,
        );
      })}

      {cargosByPort?.map((port, i) => {
        return renderCargoGroup(
          i,
          cargosByPort.length,
          routePlanStrings.receivingWarehouse,
          strings.cargosContent,
          (style) => (
            <div style={{ ...style }}>
              <div>
                <b>
                  {getCityLabelByName(port.port_name)}
                  {strings.warehouse}
                </b>
              </div>

              <div className={classes.textGray500} style={{ marginBottom: '0.5rem' }}>
                {strings.warehouseHint()}
              </div>
            </div>
          ),
          port.cargos,
        );
      })}
    </div>
  );
};
